<template>
  <div class="blog-wrapper">
    <b-overlay
        :show="isLoading"
        rounded="sm"
    >
      <b-row class="blog-list-wrapper">
        <!-- Импорт плейлиста (cover-import) доступен только админу -->
        <b-card v-if="!$can('manage', 'all')" no-body class="col-md-12">
          <b-card-body>
            <span class="text-muted">Поиск каверов по плейлистам доступен только администраторам.</span>
          </b-card-body>
        </b-card>
        <b-card v-if="$can('manage', 'all')" no-body class="col-md-12">
          <b-card-header class="pb-50">
            <h5>Поиск Каверов</h5>
          </b-card-header>
          <b-card-body>
            <b-row class="mb-1">
              <b-col cols="12">
                <b-form-radio-group
                    v-model="mode"
                    :options="modeOptions"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="10" md="10" class="mb-md-0 mb-2">
                <b-form-input
                    v-if="['yandex', 'spotify', 'vk'].includes(mode)"
                    v-model="playlistUrl"
                    placeholder="https://..."
                    @keyup.enter="parsePlaylist"
                />
                <b-form-textarea
                    v-if="mode === 'text'"
                    v-model="tracklistText"
                    placeholder="Artist - Title (каждый трек на новой строке)"
                    rows="5"
                />
              </b-col>
              <b-col cols="2" md="2">
                <b-button
                    variant="primary"
                    @click="parsePlaylist"
                    :disabled="isLoading"
                >
                  Найти
                </b-button>
              </b-col>
            </b-row>
            <b-row v-if="errorMessage" class="mt-1">
              <b-col cols="12">
                <span class="text-danger">{{ errorMessage }}</span>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- Playlist info -->
        <b-card
            v-if="playlistInfo"
            no-body
            class="col-md-12"
        >
          <b-card-body>
            <h4 v-if="playlistInfo.title">{{ playlistInfo.title }}</h4>
            <p v-if="playlistInfo.description" v-html="playlistInfo.description" />
            <span v-if="playlistInfo.count">Треков: {{ playlistInfo.count }}</span>
          </b-card-body>
        </b-card>

        <!-- Results table -->
        <b-card
            v-if="tracks.length > 0"
            no-body
            class="col-md-12"
        >
          <div class="table-responsive">
            <table class="table b-table position-relative">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Artists</th>
                  <th>Title</th>
                  <th>Оригинал</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                <covers-search-row
                    v-for="(track, index) in tracks"
                    :key="index"
                    :track="track"
                    :state="rowStates[index]"
                    :index="index"
                    @save="saveOriginal(index)"
                    @add-track="addTrackToDb(index)"
                    @search="onSearch($event.search, $event.loading, index)"
                    @apply="onApplyOriginal(index, $event)"
                />
              </tbody>
            </table>
          </div>
        </b-card>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BButton,
  BOverlay,
} from 'bootstrap-vue'
import Vue from 'vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import { useToast } from 'vue-toastification/composition'
import CoversSearchRow from './CoversSearchRow.vue'
import { createCoverLinkAfterSubmission, isSubmissionResponse, notifySubmitted } from '@/libs/moderation'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BButton,
    BOverlay,
    CoversSearchRow,
  },
  setup() {
    const STORE_MODULE_NAME = 'covers'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const mode = ref('yandex')
    const playlistUrl = ref('')
    const tracklistText = ref('')
    const isLoading = ref(false)
    const playlistInfo = ref(null)
    const tracks = ref([])
    const rowStates = ref([])
    const errorMessage = ref('')

    const getErrorText = (error) => error.response?.data?.message
      || error.response?.data?.error
      || error.message
      || 'Произошла ошибка'

    const modeOptions = [
      { text: 'Yandex', value: 'yandex' },
      { text: 'Spotify', value: 'spotify' },
      { text: 'VK', value: 'vk' },
      { text: 'Список треков', value: 'text' },
    ]

    const parsePlaylist = async () => {
      isLoading.value = true
      errorMessage.value = ''
      playlistInfo.value = null
      tracks.value = []
      rowStates.value = []

      const payload = { source: mode.value }
      if (mode.value === 'text') {
        payload.text = tracklistText.value
      } else {
        payload.link = playlistUrl.value
      }

      try {
        const response = await store.dispatch('covers/coverImport', payload)
        const data = response.data

        if (data.title || data.description || data.count) {
          playlistInfo.value = {
            title: data.title,
            description: data.description,
            count: data.count,
          }
        }

        tracks.value = data.tracks || []
        rowStates.value = tracks.value.map(track => ({
          selectedOriginal: null,
          isEditing: false,
          isSaving: false,
          isSaved: false,
          isAddingTrack: false,
          isSearching: false,
          searchOptions: (track.search_results || []).map(sr => ({
            id: sr.id,
            title: sr.title,
            artist_id: sr.artist_id,
            artist_name: sr.artist_name,
            is_cover: sr.is_cover,
            has_covers: sr.has_covers,
          })),
        }))
      } catch (error) {
        errorMessage.value = getErrorText(error)
      } finally {
        isLoading.value = false
      }
    }

    let searchTimers = {}
    const onSearch = (search, loading, rowIndex) => {
      if (!search || search.length < 2) return

      if (searchTimers[rowIndex]) clearTimeout(searchTimers[rowIndex])

      searchTimers[rowIndex] = setTimeout(async () => {
        rowStates.value[rowIndex].isSearching = true
        loading(true)
        try {
          const searchParams = { q: search, limit: 15 }
          const trackId = tracks.value[rowIndex].id
          if (trackId) searchParams.exclude_id = trackId
          const response = await store.dispatch('covers/searchTracksInDb', searchParams)
          const items = response.data.data || response.data || []
          const results = items.map(item => ({
            id: item.id,
            title: item.title,
            artist_name: item.artist || item.artist_name,
            is_cover: item.is_cover,
            has_covers: item.has_covers,
          }))

          rowStates.value[rowIndex].searchOptions = results
        } catch (error) {
          // silent
        } finally {
          rowStates.value[rowIndex].isSearching = false
          loading(false)
        }
      }, 1000)
    }

    // Возвращает { trackId } либо, если автору трек ушёл на одобрение, { submissionId }
    const ensureTrackInDb = async (rowIndex) => {
      const track = tracks.value[rowIndex]
      if (track.id) return { trackId: track.id }

      const firstArtist = track.artists && track.artists[0]
      const artistId = firstArtist && firstArtist.id ? firstArtist.id : null

      if (artistId) {
        const res = await store.dispatch('covers/addTrack', { id: artistId, queryParams: { title: track.title } })
        if (isSubmissionResponse(res)) {
          return { submissionId: res.data.submission.id }
        }
        const newTrackId = res.data.track_id || (res.data.data ? res.data.data.id : res.data.id)
        Vue.set(track, 'id', newTrackId)
        return { trackId: newTrackId }
      }

      const payload = { title: track.title }
      if (firstArtist && firstArtist.name) {
        payload.artist = firstArtist.name
      }

      const res = await store.dispatch('covers/createTrackWithoutArtist', payload)
      if (isSubmissionResponse(res)) {
        return { submissionId: res.data.submission.id }
      }
      const newTrackId = res.data.track_id || (res.data.data ? res.data.data.id : res.data.id)
      Vue.set(track, 'id', newTrackId)
      return { trackId: newTrackId }
    }

    const saveOriginal = async (rowIndex) => {
      const state = rowStates.value[rowIndex]
      if (!state.selectedOriginal) return

      state.isSaving = true
      state.isSaved = false
      errorMessage.value = ''

      try {
        const { trackId, submissionId } = await ensureTrackInDb(rowIndex)

        // Автор: кавер (трек строки) ушёл на одобрение — связь тоже заявкой, кавером выступает заявка на трек
        if (submissionId) {
          notifySubmitted(toast)
          await createCoverLinkAfterSubmission(store, toast, {
            original_track_id: state.selectedOriginal.id,
            cover_submission_id: submissionId,
          })
          return
        }

        if (!trackId) {
          throw new Error('Не удалось создать трек')
        }

        const response = await store.dispatch('covers/setTrackOriginal', {
          id: trackId,
          original_id: state.selectedOriginal.id,
        })
        // Автор: установка оригинала ушла на одобрение — строку «сохранённой» не помечаем
        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
          return
        }

        state.isSaved = true
        state.isEditing = false
        Vue.set(tracks.value[rowIndex], 'original_track_id', state.selectedOriginal.id)
        Vue.set(tracks.value[rowIndex], 'original_title', state.selectedOriginal.title)
      } catch (error) {
        errorMessage.value = getErrorText(error)
      } finally {
        state.isSaving = false
      }
    }

    const addTrackToDb = async (rowIndex) => {
      const state = rowStates.value[rowIndex]
      state.isAddingTrack = true
      errorMessage.value = ''

      try {
        const { submissionId } = await ensureTrackInDb(rowIndex)
        // Автор: трек ушёл на одобрение — в строке id не появляется
        if (submissionId) notifySubmitted(toast)
      } catch (error) {
        errorMessage.value = getErrorText(error)
      } finally {
        state.isAddingTrack = false
      }
    }

    const onApplyOriginal = (rowIndex, payload) => {
      const text = [payload.original_artist, payload.original_title].filter(Boolean).join(' ')
      if (!text) return
      rowStates.value[rowIndex].isEditing = true
      onSearch(text, () => {}, rowIndex)
    }

    return {
      mode,
      modeOptions,
      playlistUrl,
      tracklistText,
      isLoading,
      playlistInfo,
      tracks,
      rowStates,
      errorMessage,
      parsePlaylist,
      onSearch,
      saveOriginal,
      addTrackToDb,
      onApplyOriginal,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
